import { useMemo } from 'react';
import { omitBy, isNil } from 'lodash';

import { ensureDefined } from '@e-socna/utils';
import { CodingStrategyEnum } from '@e-socna/data-layer';
import { EMPTY_STRING } from '@e-socna/empty';
import { EMPTY_ID } from '@e-socna/empty-client';

import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

import type {
  CostForm_Cost,
  CostForm_CostData,
} from '../fragments/cost.fragment';

export const useInitialValuesFactory = (pInitialValues?: CostForm_CostData) => {
  const { generateId } = useIdFactory();

  const referencesContext = useReferencesContext();

  const { defaultCostType, defaultCurrency, defaultGenericStatus } =
    ensureDefined({
      defaultCostType: referencesContext.defaultCostType,
      defaultCurrency: referencesContext.defaultCurrency,
      defaultGenericStatus: referencesContext.defaultGenericStatus,
    });

  const { codingStrategiesCodeMap } = referencesContext;

  const initialValues: CostForm_Cost = useMemo(
    () => ({
      __typename: defaultCostType.code,
      _id: generateId(),
      code: EMPTY_STRING,
      label: EMPTY_STRING,
      title: EMPTY_STRING,
      content: EMPTY_STRING,
      kind: defaultCostType.code,
      typeId: defaultCostType._id,
      costMarginId: EMPTY_ID,
      statusId: defaultGenericStatus._id,
      value: 1,
      ...omitBy(pInitialValues, isNil),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        kind: CodingStrategyEnum.CodingConfigManual,
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        code: EMPTY_STRING,
        ...omitBy(pInitialValues?.codingConfig, isNil),
      },
      amount: (pInitialValues && 'amount' in pInitialValues
        ? pInitialValues.amount
        : null) ?? {
        __typename: 'Amount' as const,
        value: 1,
        currencyId: defaultCurrency._id,
      },
    }),
    [
      codingStrategiesCodeMap.CodingConfigManual._id,
      defaultCostType._id,
      defaultCostType.code,
      defaultCurrency._id,
      defaultGenericStatus._id,
      generateId,
      pInitialValues,
    ],
  );

  return { initialValues };
};
